import { useState } from "react";
import { motion } from "framer-motion";
import { Home as HomeIcon, Truck, MessageCircle, Gauge, Check, ArrowRight, Star, Repeat, Store, FileText, Fuel, KeyRound, Briefcase, Camera } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Container, Overline } from "@/components/Sections";
import { Reveal, MaskLine } from "@/components/Reveal";
import { useQuote } from "@/components/QuoteDialog";
import { IMAGES } from "@/data/site";

const H2 = ({ children }) => (
  <h2 className="font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-3 leading-[1.1]">{children}</h2>
);

const VEHICLES = ["Moto", "Scooter", "Motorino / ciclomotore", "Moto d'epoca", "Altro"];

const MotoForm = () => {
  const { openQuote } = useQuote();
  const [f, setF] = useState({ partenza: "", destinazione: "", modello: "", tipo: "Moto" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const input = "w-full h-13 px-4 rounded-xl bg-zinc-50 border border-zinc-200 outline-none focus:border-[#FF5A00] focus:bg-white transition-colors text-[15px] text-[#0A0A0A] placeholder:text-zinc-500";
  const submit = () => openQuote({
    servizio: "moto", partenza: f.partenza, destinazione: f.destinazione,
    messaggio: `Veicolo: ${f.tipo}${f.modello ? ` — ${f.modello}` : ""}`,
  });
  return (
    <div className="bg-white rounded-[1.75rem] p-6 md:p-8 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.45)]" data-testid="moto-form">
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF5A00]">Preventivo gratuito</p>
      <h3 className="font-head text-2xl font-extrabold tracking-tight mt-1 text-[#0A0A0A]">Quanto costa spedire la tua moto?</h3>
      <div className="grid grid-cols-2 gap-3 mt-5">
        <input className={input} placeholder="Da (es. Milano)" value={f.partenza} onChange={set("partenza")} data-testid="moto-form-da" />
        <input className={input} placeholder="A (es. Roma)" value={f.destinazione} onChange={set("destinazione")} data-testid="moto-form-a" />
        <input className={`${input} col-span-2`} placeholder="Modello (es. Ducati Monster)" value={f.modello} onChange={set("modello")} data-testid="moto-form-modello" />
        <div className="col-span-2 flex flex-wrap gap-2">
          {VEHICLES.map((v) => (
            <button key={v} type="button" onClick={() => setF({ ...f, tipo: v })} data-testid={`moto-form-tipo-${v.split(" ")[0].toLowerCase()}`}
              className={`px-3.5 py-2 rounded-full text-sm font-semibold border transition-colors ${f.tipo === v ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-zinc-200 text-zinc-600 hover:border-zinc-400"}`}>
              {v}
            </button>
          ))}
        </div>
        <button onClick={submit} data-testid="moto-form-submit"
          className="col-span-2 h-14 mt-1 rounded-xl bg-[#FF5A00] text-white font-bold flex items-center justify-center gap-2 hover:bg-[#E04F00] transition-colors">
          Richiedi preventivo moto <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
};

export const MotoHero = () => (
  <section className="relative pt-32 md:pt-40 pb-20 md:pb-28 bg-[#121212] text-white overflow-hidden" data-testid="moto-hero">
    <motion.img src={IMAGES.moto} alt="Moto caricata su mezzo AXAL" className="absolute inset-0 w-full h-full object-cover opacity-45"
      initial={{ scale: 1.12 }} animate={{ scale: 1 }} transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }} />
    <div className="absolute inset-0 bg-gradient-to-r from-[#121212] via-[#121212]/80 to-[#121212]/30" />
    <Container className="relative grid lg:grid-cols-[1.15fr_1fr] gap-12 items-center">
      <div>
        <Overline>Spedizione moto · Tutta Italia</Overline>
        <h1 className="font-head text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight mt-4 leading-[1.03]">
          <MaskLine delay={0.1}>La tua moto</MaskLine>
          <MaskLine delay={0.22}><span className="text-[#FF5A00]">viaggia con AXAL.</span></MaskLine>
        </h1>
        <p className="text-zinc-300 text-lg mt-6 max-w-xl leading-relaxed">
          Ritiro e consegna a domicilio per moto, scooter e motorini, con mezzi e personale AXAL. Scegli la velocità: Standard, Espresso, Urgente o Dedicato.
        </p>
        <div className="flex flex-wrap gap-x-6 gap-y-2 mt-8 text-sm font-medium text-white/85">
          <span className="flex items-center gap-2"><Star size={16} className="text-[#FF5A00] fill-[#FF5A00]" /> 4,9/5 Google</span>
          <span className="flex items-center gap-2"><Check size={16} className="text-[#FF5A00]" /> Da casa a casa</span>
          <span className="flex items-center gap-2"><Check size={16} className="text-[#FF5A00]" /> Assistenza WhatsApp</span>
        </div>
      </div>
      <Reveal delay={0.2}><MotoForm /></Reveal>
    </Container>
  </section>
);

const PERKS = [
  { icon: HomeIcon, t: "Ritiro a domicilio", d: "Veniamo noi a prendere la moto e la consegniamo dove ci indichi." },
  { icon: Truck, t: "Mezzi e personale AXAL", d: "La tua moto viaggia sui nostri mezzi, con i nostri operatori." },
  { icon: MessageCircle, t: "Assistenza diretta", d: "Un contatto WhatsApp e telefonico per tutta la durata del trasporto." },
  { icon: Gauge, t: "Tu scegli i tempi", d: "Quattro livelli di servizio, dallo Standard al Dedicato." },
];

export const MotoPerks = () => (
  <section className="py-16 md:py-20 border-b border-zinc-100" data-testid="moto-perks">
    <Container className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
      {PERKS.map((p, i) => (
        <Reveal key={p.t} delay={i * 0.08}>
          <div className="flex gap-4">
            <div className="w-12 h-12 shrink-0 rounded-xl bg-[#FF5A00]/10 flex items-center justify-center text-[#FF5A00]"><p.icon size={22} /></div>
            <div>
              <h3 className="font-head text-lg font-bold">{p.t}</h3>
              <p className="text-zinc-500 text-[15px] mt-1 leading-relaxed">{p.d}</p>
            </div>
          </div>
        </Reveal>
      ))}
    </Container>
  </section>
);

const WHAT = [
  { t: "Moto", d: "Stradali, sportive, naked, touring, custom ed enduro." },
  { t: "Scooter", d: "Dal 50cc ai maxi scooter." },
  { t: "Motorini e ciclomotori", d: "Anche per brevi tratte in città." },
  { t: "Moto d'epoca e da collezione", d: "Con attenzione dedicata al carico e al fissaggio." },
];

export const MotoWhat = () => (
  <section className="py-24 md:py-32" data-testid="moto-what">
    <Container className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <Reveal>
        <div className="relative rounded-[2rem] overflow-hidden aspect-[4/3]">
          <img src={IMAGES.motoScooter} alt="Moto e scooter su mezzo AXAL" className="absolute inset-0 w-full h-full object-cover" />
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <Overline>Cosa spediamo</Overline>
        <H2>Ogni veicolo a due ruote.</H2>
        <div className="mt-8 divide-y divide-zinc-100 border-y border-zinc-100">
          {WHAT.map((w, i) => (
            <div key={w.t} className="py-4 flex items-start gap-5">
              <span className="font-head text-sm font-bold text-[#FF5A00] pt-1">0{i + 1}</span>
              <div>
                <h3 className="font-head text-lg font-bold">{w.t}</h3>
                <p className="text-zinc-500 text-[15px] mt-0.5">{w.d}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="text-zinc-500 text-[15px] mt-5">Hai un veicolo diverso (quad, sidecar, mini moto)? Scrivici e valutiamo insieme.</p>
      </Reveal>
    </Container>
  </section>
);

const MSTEPS = [
  { t: "Richiedi il preventivo", d: "Indica tratta, modello e velocità che preferisci." },
  { t: "Confermi e fissiamo il ritiro", d: "Ti comunichiamo per tempo la data di ritiro e la consegna prevista." },
  { t: "Carichiamo e fissiamo la moto", d: "I nostri operatori la caricano e la bloccano in sicurezza sul mezzo." },
  { t: "Consegna a domicilio", d: "Ti avvisiamo prima dell'arrivo e la moto ti aspetta dove hai scelto." },
];

export const MotoSteps = () => (
  <section className="py-24 md:py-32 bg-[#121212] text-white relative overflow-hidden" data-testid="moto-steps">
    <div className="absolute inset-0 noise-overlay opacity-[0.05]" />
    <Container className="relative">
      <Reveal className="max-w-xl mb-14">
        <Overline>Come funziona</Overline>
        <H2>Dal tuo garage al tuo garage.</H2>
      </Reveal>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        {MSTEPS.map((s, i) => (
          <Reveal key={s.t} delay={i * 0.1}>
            <div className="h-full rounded-2xl border border-white/10 p-7 hover:border-[#FF5A00]/60 transition-colors">
              <span className="font-head text-5xl font-extrabold text-[#FF5A00]">0{i + 1}</span>
              <h3 className="font-head text-xl font-bold mt-4">{s.t}</h3>
              <p className="text-zinc-400 text-[15px] mt-2 leading-relaxed">{s.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </Container>
  </section>
);

const PREP = [
  { icon: Briefcase, t: "Segnalaci gli accessori", d: "Borse laterali, bauletto o scatole extra: così riserviamo lo spazio giusto sul mezzo." },
  { icon: Fuel, t: "Poca benzina nel serbatoio", d: "Non serve svuotarlo: basta lasciarne il meno possibile." },
  { icon: FileText, t: "Copia del libretto", d: "Tienila pronta al momento del ritiro." },
  { icon: KeyRound, t: "Chiavi o niente blocchi", d: "Lascia le chiavi oppure rimuovi bloccadisco, bloccasterzo e catene." },
  { icon: Camera, t: "Qualche foto prima del ritiro", d: "Utile per documentare lo stato della moto alla partenza." },
];

export const MotoPrep = () => (
  <section className="py-24 md:py-32" data-testid="moto-prep">
    <Container className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <Reveal className="lg:order-2">
        <div className="relative rounded-[2rem] overflow-hidden aspect-[4/3]">
          <img src={IMAGES.motoStrap} alt="Operatore AXAL fissa una moto" className="absolute inset-0 w-full h-full object-cover" />
        </div>
      </Reveal>
      <Reveal delay={0.1} className="lg:order-1">
        <Overline>Come preparare la moto</Overline>
        <H2>Tu non devi fare quasi nulla.</H2>
        <ul className="mt-8 space-y-5">
          {PREP.map((p) => (
            <li key={p.t} className="flex gap-4">
              <div className="w-10 h-10 shrink-0 rounded-full bg-[#FF5A00]/10 flex items-center justify-center text-[#FF5A00]"><p.icon size={18} /></div>
              <div>
                <h3 className="font-bold">{p.t}</h3>
                <p className="text-zinc-500 text-[15px] mt-0.5">{p.d}</p>
              </div>
            </li>
          ))}
        </ul>
      </Reveal>
    </Container>
  </section>
);

const OFFERS = [
  { icon: Repeat, t: "Andata e ritorno", d: "Per vacanze, eventi o giornate in pista: organizziamo anche il rientro della moto. Chiedi la tariffa combinata.", msg: "Offerta andata e ritorno" },
  { icon: Store, t: "Concessionari e officine", d: "Spedizioni ricorrenti dal negozio al cliente finale, con un referente dedicato. Chiedi le condizioni riservate.", msg: "Spedizioni moto per concessionari / officine" },
];

export const MotoOffers = () => {
  const { openQuote } = useQuote();
  return (
    <section className="py-24 md:py-32 bg-[#F4F4F5]" data-testid="moto-offers">
      <Container>
        <Reveal className="mb-12">
          <Overline>Soluzioni dedicate</Overline>
          <H2>Più moto, più comodità.</H2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-5">
          {OFFERS.map((o, i) => (
            <Reveal key={o.t} delay={i * 0.1}>
              <div className="h-full bg-white rounded-[1.5rem] p-8 md:p-10 border border-zinc-100 flex flex-col">
                <div className="w-14 h-14 rounded-2xl bg-[#0A0A0A] text-[#FF5A00] flex items-center justify-center"><o.icon size={24} /></div>
                <h3 className="font-head text-2xl md:text-3xl font-extrabold tracking-tight mt-6">{o.t}</h3>
                <p className="text-zinc-500 mt-3 text-[15px] leading-relaxed max-w-md">{o.d}</p>
                <button onClick={() => openQuote({ servizio: "moto", messaggio: o.msg })} data-testid={`moto-offer-${i}`}
                  className="mt-auto pt-7 inline-flex items-center gap-2 font-bold hover:text-[#FF5A00] transition-colors group self-start">
                  Richiedi informazioni <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
};

const FAQ = [
  { q: "Quanto costa spedire una moto?", a: "Il prezzo dipende dalla tratta, dal tipo di veicolo e dalla velocità scelta. Il preventivo è gratuito: compila il modulo o scrivici su WhatsApp e ti rispondiamo con la cifra esatta." },
  { q: "Quanto tempo ci vuole?", a: "Servizio Standard circa 15/18 giorni lavorativi, Espresso circa 12/13 (+25%), Urgente circa 7/8 (+40%), previa verifica della fattibilità logistica. Il trasporto Dedicato si concorda su richiesta." },
  { q: "Devo imballare la moto?", a: "No. Il carico e il fissaggio sul mezzo li gestiscono i nostri operatori." },
  { q: "Che documenti servono?", a: "Una copia del libretto di circolazione da consegnare al momento del ritiro." },
  { q: "Spedite anche scooter e motorini?", a: "Sì, spediamo moto, scooter, motorini e ciclomotori di ogni cilindrata." },
  { q: "Arrivate anche nelle isole?", a: "Verifichiamo caso per caso la fattibilità per Sicilia, Sardegna e isole minori: indicacelo nella richiesta." },
];

export const MotoFaq = () => (
  <section className="py-24 md:py-32" data-testid="moto-faq">
    <Container className="grid lg:grid-cols-[1fr_1.4fr] gap-12">
      <Reveal>
        <Overline>Domande frequenti</Overline>
        <H2>Tutto quello che vuoi sapere.</H2>
      </Reveal>
      <Reveal delay={0.1}>
        <Accordion type="single" collapsible className="border-t border-zinc-200">
          {FAQ.map((f, i) => (
            <AccordionItem key={f.q} value={`f${i}`} className="border-zinc-200" data-testid={`moto-faq-${i}`}>
              <AccordionTrigger className="font-head text-lg font-bold text-left hover:no-underline hover:text-[#FF5A00] py-5">{f.q}</AccordionTrigger>
              <AccordionContent className="text-zinc-500 text-[15px] leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </Container>
  </section>
);

